import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaSearch, FaArrowRight, FaStar } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import {
  searchSections, getFrequentSections, TEAMS_HUB, HOME,
} from '../../config/teams';

/* ============================================================
   Buscador global (command palette). Se abre con Ctrl/⌘+K o
   desde el disparador de la sidebar / barra móvil. Indexa todas
   las secciones y navega con teclado.
   ============================================================ */

const mono = 'var(--font-mono)';

const PaletteContext = createContext({ open: () => {}, close: () => {}, isOpen: false });
export const useCommandPalette = () => useContext(PaletteContext);

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const popIn = keyframes`
  from { opacity: 0; transform: translateY(-8px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-palette);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 12vh 1rem 1rem;
  background: rgba(4, 9, 18, 0.62);
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.14s ease;

  @media (max-width: 768px) { padding: 8vh 0.75rem 1rem; }
`;

const Box = styled.div`
  width: 100%;
  max-width: 560px;
  background:
    linear-gradient(180deg, rgba(0, 180, 216, 0.06), transparent 22%),
    #131f30;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: ${popIn} 0.16s ease;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border-soft);

  > svg { color: var(--btn-primary); font-size: 0.9rem; flex-shrink: 0; }

  input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--text-primary);
    font-family: inherit;
    font-size: 1rem;
    &::placeholder { color: var(--text-disabled); }
  }

  kbd {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.08em;
    color: var(--text-disabled);
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    padding: 2px 6px;
    flex-shrink: 0;
  }
`;

const Results = styled.div`
  max-height: 52vh;
  overflow-y: auto;
  padding: 0.4rem;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
`;

const GroupLabel = styled.div`
  font-family: ${mono};
  font-size: 0.58rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--text-disabled);
  padding: 0.6rem 0.7rem 0.3rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  > svg { font-size: 0.62rem; }
`;

const Item = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.55rem 0.7rem;
  background: ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.12)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(0, 180, 216, 0.28)' : 'transparent')};
  border-radius: var(--radius-sm);
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover { background: rgba(255, 255, 255, 0.04); }

  .ico {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    border-radius: var(--radius-sm);
    font-size: 0.82rem;
    color: ${({ $color }) => $color || 'var(--btn-primary)'};
    background: ${({ $color }) => ($color ? `${$color}1a` : 'rgba(0,180,216,0.1)')};
    border: 1px solid ${({ $color }) => ($color ? `${$color}44` : 'rgba(0,180,216,0.28)')};
  }

  .body { flex: 1; min-width: 0; }
  .label {
    display: block;
    font-size: 0.9rem;
    color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .meta {
    display: block;
    font-size: 0.72rem;
    color: var(--text-disabled);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .go {
    color: var(--btn-primary);
    font-size: 0.72rem;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    flex-shrink: 0;
  }
`;

const EmptyRow = styled.div`
  padding: 1.6rem 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.85rem;
`;

// Ítems fijos que siempre aparecen (sin query): Inicio y Equipos.
const PINNED_NAV = [
  { ...HOME, desc: 'Centro de mando', team: 'General', teamColor: 'var(--btn-primary)' },
  { ...TEAMS_HUB, desc: 'Todas las áreas y herramientas', team: 'General', teamColor: 'var(--btn-primary)' },
];

function Palette({ onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Resultados: sin query → frecuentes + navegación fija. Con query → búsqueda.
  const { groups, flat } = useMemo(() => {
    const q = query.trim();
    if (!q) {
      const freq = getFrequentSections(role, 6);
      const g = [
        { label: 'Ir a', icon: null, items: PINNED_NAV },
      ];
      if (freq.length) g.push({ label: 'Lo que más usás', icon: FaStar, items: freq });
      return { groups: g, flat: g.flatMap((x) => x.items) };
    }
    const hits = searchSections(q, role);
    const g = [{ label: `${hits.length} resultado${hits.length === 1 ? '' : 's'}`, icon: null, items: hits }];
    return { groups: g, flat: hits };
  }, [query, role]);

  useEffect(() => { setActive(0); }, [query]);

  const go = useCallback((to) => {
    if (!to) return;
    onClose();
    navigate(to);
  }, [navigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[active]?.to);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Mantener el ítem activo a la vista.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  let idx = -1;

  return (
    <Overlay onMouseDown={onClose}>
      <Box onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Buscar en la plataforma">
        <InputRow>
          <FaSearch />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Buscar una sección… (ej: reputación, vecinos, prensa)"
            aria-label="Buscar una sección"
            autoComplete="off"
            spellCheck="false"
          />
          <kbd>ESC</kbd>
        </InputRow>

        <Results ref={listRef}>
          {flat.length === 0 && (
            <EmptyRow>Nada coincide con “{query}”. Probá con otra palabra.</EmptyRow>
          )}

          {groups.map((group) => (
            <React.Fragment key={group.label}>
              <GroupLabel>
                {group.icon ? <group.icon /> : null}
                {group.label}
              </GroupLabel>
              {group.items.map((item) => {
                idx += 1;
                const i = idx;
                const isActive = i === active;
                const Icon = item.icon;
                return (
                  <Item
                    key={item.to}
                    $active={isActive}
                    $color={item.teamColor}
                    data-active={isActive}
                    onMouseMove={() => setActive(i)}
                    onClick={() => go(item.to)}
                  >
                    <span className="ico">{Icon ? <Icon /> : <FaArrowRight />}</span>
                    <span className="body">
                      <span className="label">{item.label}</span>
                      <span className="meta">
                        {item.team}{item.desc ? ` · ${item.desc}` : ''}
                      </span>
                    </span>
                    <FaArrowRight className="go" />
                  </Item>
                );
              })}
            </React.Fragment>
          ))}
        </Results>
      </Box>
    </Overlay>
  );
}

/* Provider global: monta el atajo de teclado y expone open/close. */
export function CommandPaletteProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <PaletteContext.Provider value={value}>
      {children}
      {isOpen && <Palette onClose={close} />}
    </PaletteContext.Provider>
  );
}

export default CommandPaletteProvider;
